import { Injectable } from '@angular/core';
import { Critter } from '@app/infrastructure/models/critter';
import { BehaviorSubject, Observable, Subscription, of } from 'rxjs';
import { CritterService } from '../api/critter.service';
import { map, switchMap, tap } from 'rxjs/operators';
import { StudentCritterService } from '../api/student-critter.service';

@Injectable({
  providedIn: 'root',
})
export class CritterState {
  private critterList: BehaviorSubject<Critter[]> = new BehaviorSubject<
    Critter[]
  >([]);

  constructor(
    private critterService: CritterService,
    private studentCritterService: StudentCritterService,
  ) {}

  public get critters(): Critter[] {
    return this.critterList.getValue();
  }

  public get critters$(): Observable<Critter[]> {
    return this.critterList.asObservable();
  }

  public getCritterList(): Critter[] {
    return this.critterList.getValue();
  }

  public getCritterById(id: number): Critter {
    return this.critters.find((c) => c.id === id);
  }

  public refreshCritterCollectionList(): Observable<Critter[]> {
    if (this.critters.length > 0) return of(this.critters);
    return this.studentCritterService.getStudentCritters().pipe(
      map((critters: Critter[]) => {
        this.critterList.next(critters);
        return critters;
      }),
      switchMap((critters: Critter[]) => of(critters)),
    );
  }

  public refreshCritterList(): Observable<Critter[]> {
    return this.critterService.getList().pipe(
      map((critters: Critter[]) => {
        this.critterList.next(critters);
        return critters;
      }),
      switchMap((critters: Critter[]) => of(critters)),
    );
  }

  public addCritterToCollection(critter: Critter): void {
    const currentCritters = this.critterList.getValue();
    currentCritters.push(critter);
    this.critterList.next(currentCritters);
  }

  public addCritter(critter: Critter): Observable<Critter> {
    return this.critterService.create(critter).pipe(
      tap((critter: Critter) => {
        const currentCritters = this.critterList.getValue();
        currentCritters.push(critter);
        this.critterList.next(currentCritters);
      }),
    );
  }

  public updateCritter(critter: Critter): void {
    const currentCritters = this.critterList.getValue();
    const critterIndex = currentCritters.findIndex((c) => c.id === critter.id);
    this.critterService
      .update(critter, critter.id)
      .subscribe((updatedCritter: Critter) => {
        currentCritters[critterIndex] = updatedCritter;
        this.critterList.next(currentCritters);
      });
  }

  public removeCritter(critter: Critter): void {
    const currentCritters = this.critterList.getValue();
    const critterIndex = currentCritters.findIndex((c) => c.id === critter.id);
    currentCritters.splice(critterIndex, 1);
    this.critterList.next(currentCritters);
    this.critterService.delete(critter).subscribe((updatedCritter: Critter) => {
      this.critterList.next(currentCritters);
    });
  }
}
